import { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, BookmarkCheck, Leaf, LifeBuoy, LoaderCircle, MapPin, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import RecoveryOptions from "./RecoveryOptions";

const statusTone = (status = "") => {
  const value = status.toLowerCase();
  if (value.includes("arriv") || value.includes("land")) return "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (value.includes("cancel") || value.includes("divert")) return "bg-red-100 text-red-800 border-red-300";
  if (value.includes("delay")) return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-blue-100 text-blue-800 border-blue-300";
};

const shortTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function SavedFlights({ refreshKey = 0 }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null); // saved flight id currently refreshing

  const load = useCallback(() => {
    api.get("/flights/saved").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const refresh = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/flights/saved/${id}/refresh`);
      setItems((list) => list.map((f) => (f.id === id ? r.data : f)));
      toast.success(r.data.changes?.length ? `Updated — ${r.data.changes.join(" · ")}` : "Status refreshed — no changes");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refresh this flight right now.");
    } finally {
      setBusy(null);
    }
  };

  const toggleAlerts = async (flight) => {
    const next = !flight.alerts_enabled;
    try {
      await api.patch(`/flights/saved/${flight.id}`, { alerts_enabled: next });
      setItems((list) => list.map((f) => (f.id === flight.id ? { ...f, alerts_enabled: next } : f)));
      toast.success(next ? "Delay alerts on — we'll email you and ping the bell when this flight changes." : "Delay alerts off for this flight.");
    } catch {
      toast.error("Could not update alerts.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/flights/saved/${id}`);
      setItems((list) => list.filter((f) => f.id !== id));
      toast.success("Flight removed from saved flights.");
    } catch {
      toast.error("Could not remove this flight.");
    }
  };

  if (!items || items.length === 0) return null;

  return (
    <section className="border border-zinc-300 bg-white" data-testid="saved-flights">
      <div className="border-b border-sky-900 bg-sky-800 px-5 py-5 text-white sm:px-6">
        <h2 className="font-heading font-900 text-[#4285F4] text-2xl sm:text-3xl flex items-center gap-2"><BookmarkCheck className="w-7 h-7" /> Saved Flights &amp; Alerts</h2>
        <p className="text-sm sm:text-base text-sky-100 mt-1">We check flights close to their travel date every 30 minutes and alert you when the gate, terminal, status or delay changes. On mornings you fly, we also email you a digest of the day's flights.</p>
      </div>
      <div className="p-5 sm:p-6 space-y-3">
        {items.map((f) => <SavedFlightCard key={f.id} flight={f} busy={busy === f.id} onRefresh={refresh} onToggle={toggleAlerts} onRemove={remove} />)}
      </div>
    </section>
  );
}

function SavedFlightCard({ flight, busy, onRefresh, onToggle, onRemove }) {
  const snap = flight.snapshot || {};
  const dep = snap.departure || {};
  const arr = snap.arrival || {};
  const [showRecovery, setShowRecovery] = useState(false);
  const disrupted = /cancel|delay|divert/i.test(snap.status || "") || (snap.delay_minutes || 0) >= 45;
  return (
    <article className="border border-zinc-300 bg-zinc-50" data-testid={`saved-flight-${flight.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
        <div className="font-heading font-900 text-lg" data-testid={`saved-flight-number-${flight.id}`}>{flight.airline_code} {flight.flight_number}</div>
        <div className="text-sm text-muted-foreground inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{dep.airport_code || "?"} → {arr.airport_code || "?"} · {flight.date}</div>
        <span className={`border px-2 py-0.5 text-xs font-mono uppercase ${statusTone(snap.status)}`} data-testid={`saved-flight-status-${flight.id}`}>{snap.status || "No data yet"}</span>
        <span className={`text-xs font-700 ${snap.delay_minutes > 0 ? "text-amber-700" : "text-emerald-700"}`}>
          {snap.delay_minutes == null ? "" : snap.delay_minutes > 0 ? `${snap.delay_minutes} min delay` : "On time"}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          {dep.airport_code && arr.airport_code && (
            <button onClick={() => setShowRecovery((v) => !v)} data-testid={`saved-flight-recovery-${flight.id}`}
              title="Recovery options: alternative airlines & your rights"
              className={`p-1.5 border transition-colors ${disrupted ? "border-rose-400 bg-rose-50 text-rose-700 hover:bg-rose-100" : "border-zinc-300 text-zinc-500 hover:text-rose-700 hover:border-rose-400"}`}>
              <LifeBuoy className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => onToggle(flight)} data-testid={`saved-flight-alerts-${flight.id}`}
            title={flight.alerts_enabled ? "Delay alerts on — click to turn off" : "Delay alerts off — click to turn on"}
            className={`p-1.5 border transition-colors ${flight.alerts_enabled ? "border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-zinc-300 text-zinc-400 hover:text-zinc-700"}`}>
            {flight.alerts_enabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
          </button>
          <button onClick={() => onRefresh(flight.id)} disabled={busy} data-testid={`saved-flight-refresh-${flight.id}`} title="Refresh status"
            className="p-1.5 border border-zinc-300 text-zinc-600 hover:text-primary hover:border-primary transition-colors disabled:opacity-60">
            {busy ? <LoaderCircle className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
          <button onClick={() => onRemove(flight.id)} data-testid={`saved-flight-remove-${flight.id}`} title="Remove saved flight"
            className="p-1.5 border border-zinc-300 text-zinc-600 hover:text-destructive hover:border-destructive transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 border-t border-zinc-200 bg-white px-4 py-2 text-xs text-zinc-600">
        <span>Dep: T{dep.terminal || "—"} · Gate {dep.gate || "—"} · {timeOnly(dep.estimated || dep.scheduled)}</span>
        <span>Arr: T{arr.terminal || "—"} · Gate {arr.gate || "—"} · {timeOnly(arr.estimated || arr.scheduled)}</span>
        {flight.co2 && (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-600" data-testid={`saved-flight-co2-${flight.id}`}
            title={`~${flight.co2.distance_km} km ${flight.co2.haul} · like driving ${flight.co2.car_km.toLocaleString()} km by car · ${flight.co2.trees_year} trees absorb this in a year`}>
            <Leaf className="w-3.5 h-3.5" /> ~{flight.co2.co2_kg} kg CO2 · {flight.co2.trees_year} trees/yr
          </span>
        )}
        <span className="ml-auto text-muted-foreground">Checked {shortTime(flight.snapshot_at)}</span>
      </div>
      {flight.last_change?.items?.length > 0 && (
        <div className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900" data-testid={`saved-flight-change-${flight.id}`}>
          <strong>Latest change ({shortTime(flight.last_change.at)}):</strong> {flight.last_change.items.join(" · ")}
        </div>
      )}
      {showRecovery && (
        <div className="border-t border-rose-200 bg-rose-50/40 px-4 py-4" data-testid={`saved-flight-recovery-panel-${flight.id}`}>
          <RecoveryOptions origin={dep.airport_code} dest={arr.airport_code}
            airline={flight.airline_code} date={flight.date} />
        </div>
      )}
    </article>
  );
}

const timeOnly = (value) => {
  if (!value) return "—";
  const time = (value.split("T")[1] || "").slice(0, 5);
  return time || value;
};
